import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";
import BRFlag from "../../assets/brazil.png";
import USFlag from "../../assets/unitedstates.png";
import ESFlag from "../../assets/esspain.png";
import ARFlag from "../../assets/arabe.png";
import clsx from "clsx";
import { getBackendUrl } from "../../config";
import packageJson from "../../../package.json";

const languageOptions = [
    { value: "pt-BR", label: "Português", icon: BRFlag },
    { value: "en", label: "English", icon: USFlag },
    { value: "es", label: "Spanish", icon: ESFlag },
    { value: "ar", label: "عربي", icon: ARFlag },
];

const useStyles = makeStyles((theme) => {
    const { mode } = theme;
    return ({
    loginPageGlobal: {
        backgroundRepeat: "no-repeat !important",
        backgroundSize: "cover !important",
        backgroundPosition: "center center !important",
        backgroundAttachment: "fixed !important",
        width: "100% !important",
        height: "100% !important",
        minHeight: "100vh !important",
        minWidth: "100vw !important",
        overflow: "hidden !important",
    },
    root: {
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "0",
        margin: "0",
        boxSizing: "border-box",
        overflow: "hidden",
        position: "relative",
        backgroundColor: theme.palette.background ? theme.palette.background.default : (theme.palette.type === "light" ? "#f5f5f5" : "#303030"),
        backgroundImage: "none",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
        },
    },

    "@keyframes float": {
        "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
        "50%": { transform: "translateY(-10px) rotate(180deg)" },
    },

    containerLogin: {
        padding: "16px",
        width: "calc(100% - 32px)",
        zIndex: 10,
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        position: "absolute",
        maxWidth: "420px",
        margin: "0 auto",

        [theme.breakpoints.down("sm")]: {
            width: "calc(100% - 40px)",
            maxWidth: "90%",
        },
        
        [theme.breakpoints.down("xs")]: {
            width: "calc(100% - 32px)",
            maxWidth: "85%",
        },
    },

    paper: {
        backgroundColor: theme.mode === "light" ? "rgba(255, 255, 255, 0.95)" : "rgba(31, 41, 55, 0.95)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.1),
      0 1px 0 rgba(255, 255, 255, 0.2) inset,
      0 0 0 1px rgba(255, 255, 255, 0.1)
    `,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "40px 30px",
        borderRadius: "20px",
        maxWidth: "420px",
        width: "100%",
        margin: "0 auto",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        boxSizing: "border-box",

        [theme.breakpoints.down("sm")]: {
            animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: "16px",
            padding: "30px 25px",
            boxSizing: "border-box",
            width: "100%",
        },
        
        [theme.breakpoints.down("xs")]: {
            padding: "25px 20px",
            borderRadius: "14px",
            boxShadow: `0 15px 30px rgba(0, 0, 0, 0.12)`,
            boxSizing: "border-box",
            width: "100%",
        },
    },

    "@keyframes slideInRight": {
        from: {
            opacity: 0,
            transform: "translateY(30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },

    "@keyframes slideInUp": {
        from: {
            opacity: 0,
            transform: "translateY(30px)",
        },
        to: {
            opacity: 1,
            transform: "translateY(0)",
        },
    },

    avatar: {
        margin: theme.spacing(1),
        backgroundColor: "#3b82f6",
    },

    form: {
        width: "100%",
        marginTop: theme.spacing(1),
    },

    submit: {
        margin: theme.spacing(3, 0, 2),
        background: mode === "light" 
            ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` 
            : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        color: "white",
        borderRadius: "12px",
        padding: "12px 0",
        fontSize: "16px",
        fontWeight: 600,
        textTransform: "none",
        boxShadow: `0 4px 12px ${theme.palette.primary.main}40`,
        border: "none",
        transition: "all 0.3s ease",
        "&:hover": {
            background: mode === "light"
                ? `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`
                : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
            transform: "translateY(-2px)",
            boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
        },
        "&:active": {
            transform: "translateY(0)",
        },
    },

    powered: {
        color: "white",
    },

    logoImg: {
        width: "100%",
        maxWidth: "280px",
        height: "auto",
        maxHeight: "80px",
        margin: "0 auto 20px auto",
        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
        content: "url(" + (theme.mode === "light"
            ? theme.calculatedLogoLight()
            : theme.calculatedLogoDark()) + ")",
    },

    iconButton: {
        position: "absolute",
        top: 15,
        right: 15,
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        color: theme.mode === "light" ? "#374151" : "#f3f4f6",
        transition: "all 0.3s ease",
        "&:hover": {
            background: "rgba(255, 255, 255, 0.2)",
            transform: "scale(1.05)",
        },
    },

    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(10px)",
            transition: "all 0.3s ease",
            "& input:-webkit-autofill": {
                transition: "background-color 5000s ease-in-out 0s",
                WebkitBoxShadow: "0 0 0 1000px #f3f4f6 inset !important",
                WebkitTextFillColor: "#374151 !important",
            },
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.9)",
            },
            "&.Mui-focused": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                boxShadow: "0 0 0 3px rgba(156, 163, 175, 0.1)",
            },
            "& fieldset": {
                borderColor: "rgba(156, 163, 175, 0.3)",
            },
            "&:hover fieldset": {
                borderColor: "rgba(156, 163, 175, 0.5)",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#6b7280",
                borderWidth: "2px",
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.mode === "light" ? "#6b7280" : "#d1d5db",
            fontWeight: 500,
            "&.Mui-focused": {
                color: theme.mode === "light" ? "#4b5563" : "#f3f4f6",
            },
        },
    },

    languageSelector: {
        position: "fixed",
        top: "20px",
        left: "20px",
        zIndex: 1000,
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        borderRadius: "12px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        padding: "8px 12px",
    },

    registerLink: {
        color: "#3b82f6",
        textDecoration: "none",
        fontWeight: 600,
        transition: "all 0.3s ease",
        "&:hover": {
            color: "#2563eb",
            textDecoration: "underline",
        },
    },

    languageDropdown: {
        display: "flex",
        alignItems: "center",
        background: "none",
        border: "none",
        color: "white",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        gap: "8px",
        transition: "opacity 0.3s ease",
        "&:hover": {
            opacity: 0.8,
        },
    },

    languageOptions: {
        position: "absolute",
        top: "100%",
        left: "0",
        marginTop: "8px",
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(20px)",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        borderRadius: "12px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        padding: "8px",
        zIndex: 1000,
        minWidth: "140px",
    },

    languageOption: {
        background: "none",
        border: "none",
        color: "#374151",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        width: "100%",
        padding: "8px 12px",
        textAlign: "left",
        borderRadius: "8px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
            background: "rgba(59, 130, 246, 0.1)",
            color: "#3b82f6",
        },
    },

    flagIcon: {
        width: 20,
        height: 15,
        borderRadius: 2,
    },

    loginVideoEmbed: {
        position: "absolute",
        zIndex: 5,
        width: 280,
        maxWidth: "calc(100vw - 32px)",
        height: 158,
        borderRadius: 8,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        [theme.breakpoints.down("sm")]: {
            width: 240,
            height: 135,
        },
    },

    versionInfo: {
        position: "fixed",
        bottom: "20px",
        right: "20px",
        zIndex: 1000,
        background: "rgba(255, 255, 255, 0.1)",
        backdropFilter: "blur(10px)",
        borderRadius: "8px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        padding: "6px 12px",
        color: "white",
        fontSize: "12px",
        fontWeight: 500,
        opacity: 0.8,
        transition: "opacity 0.3s ease",
        "&:hover": {
            opacity: 1,
        },
    },
})});

const Login = () => {
    const classes = useStyles();
    const theme = useTheme();
    const { colorMode } = useContext(ColorModeContext);
    const { appLogoFavicon, appName, mode } = colorMode || {};
    const currentMode = mode || theme.palette?.type || "light";
    const [user, setUser] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [allowSignup, setAllowSignup] = useState(false);
    const { getPublicSetting } = useSettings();
    const { handleLogin } = useContext(AuthContext);

    const [open, setOpen] = useState(false);
    const ref = useRef();
    const [enabledLanguages, setEnabledLanguages] = useState(["pt-BR", "en"]);
    const [backgroundLight, setBackgroundLight] = useState("");
    const [backgroundDark, setBackgroundDark] = useState("");
    const [loginVideoYoutube, setLoginVideoYoutube] = useState("");
    const [loginVideoPosition, setLoginVideoPosition] = useState("bottom-right");

    // Whitelabel (fundo, vídeo, etc.) é único para todas as companies: sempre buscar sem companyId (backend usa company 1)
    const getCompanyIdForSettings = () => null;

    const handleChangeInput = (e) => {
        setUser({ ...user, [e.target.name]: e.target.value });
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handlSubmit = (e) => {
        e.preventDefault();
        handleLogin(user);
    };

    useEffect(() => {
        const companyId = getCompanyIdForSettings();

        getPublicSetting("userCreation", companyId)
            .then((data) => {
                setAllowSignup(data === "enabled");
            })
            .catch((error) => {
                console.log("Error reading setting", error);
            });

        getPublicSetting("enabledLanguages", companyId)
            .then((langs) => {
                let arr = ["pt-BR", "en"];
                try {
                    if (langs) arr = JSON.parse(langs);
                } catch {}
                setEnabledLanguages(arr);
            })
            .catch(() => {
                setEnabledLanguages(["pt-BR", "en"]);
            });

        getPublicSetting("appLoginVideoYoutube", companyId)
            .then((url) => { if (url != null && String(url).trim()) setLoginVideoYoutube(String(url).trim()); })
            .catch(() => {});
        getPublicSetting("appLoginVideoPosition", companyId)
            .then((pos) => { if (pos != null && String(pos).trim()) setLoginVideoPosition(String(pos).trim()); })
            .catch(() => {});

        // ✅ CORREÇÃO: Tentar carregar do localStorage primeiro para evitar delay
        const cachedBgLight = localStorage.getItem(`backgroundLight_${companyId || 'default'}`);
        const cachedBgDark = localStorage.getItem(`backgroundDark_${companyId || 'default'}`);
        
        if (cachedBgLight) {
            // ✅ CORREÇÃO: Aplicar imediatamente do cache, depois verificar se ainda é válida
            setBackgroundLight(cachedBgLight);
            // Verificar se a URL ainda é válida em background
            const img = new Image();
            // ✅ CORREÇÃO CORS: Adicionar crossOrigin para permitir carregamento de imagens
            img.crossOrigin = "anonymous";
            img.onload = () => {
                // Imagem válida, manter no estado
                setBackgroundLight(cachedBgLight);
            };
            img.onerror = (error) => {
                console.warn("Erro ao validar imagem de fundo clara do cache:", error);
                // URL inválida, remover do cache e buscar nova
                localStorage.removeItem(`backgroundLight_${companyId || 'default'}`);
                setBackgroundLight("");
            };
            img.src = cachedBgLight;
        }
        
        if (cachedBgDark) {
            // ✅ CORREÇÃO: Aplicar imediatamente do cache, depois verificar se ainda é válida
            setBackgroundDark(cachedBgDark);
            // Verificar se a URL ainda é válida em background
            const img = new Image();
            // ✅ CORREÇÃO CORS: Adicionar crossOrigin para permitir carregamento de imagens
            img.crossOrigin = "anonymous";
            img.onload = () => {
                // Imagem válida, manter no estado
                setBackgroundDark(cachedBgDark);
            };
            img.onerror = (error) => {
                console.warn("Erro ao validar imagem de fundo escura do cache:", error);
                // URL inválida, remover do cache e buscar nova
                localStorage.removeItem(`backgroundDark_${companyId || 'default'}`);
                setBackgroundDark("");
            };
            img.src = cachedBgDark;
        }

        getPublicSetting("appLogoBackgroundLight", companyId)
            .then((bgLight) => {
                if (bgLight && String(bgLight).trim()) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + String(bgLight).trim();
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => {
                        setBackgroundLight(fullUrl);
                        localStorage.setItem(`backgroundLight_${companyId || 'default'}`, fullUrl);
                    };
                    img.onerror = () => {
                        localStorage.removeItem(`backgroundLight_${companyId || 'default'}`);
                    };
                    img.src = fullUrl;
                    setTimeout(() => {
                        if (img.complete && img.naturalHeight !== 0) {
                            setBackgroundLight(fullUrl);
                            localStorage.setItem(`backgroundLight_${companyId || 'default'}`, fullUrl);
                        }
                    }, 0);
                }
            })
            .catch(() => {});

        getPublicSetting("appLogoBackgroundDark", companyId)
            .then((bgDark) => {
                if (bgDark && String(bgDark).trim()) {
                    const backendUrl = getBackendUrl();
                    const fullUrl = backendUrl + "/public/" + String(bgDark).trim();
                    const img = new Image();
                    img.crossOrigin = "anonymous";
                    img.onload = () => {
                        setBackgroundDark(fullUrl);
                        localStorage.setItem(`backgroundDark_${companyId || 'default'}`, fullUrl);
                    };
                    img.onerror = () => {
                        localStorage.removeItem(`backgroundDark_${companyId || 'default'}`);
                    };
                    img.src = fullUrl;
                    setTimeout(() => {
                        if (img.complete && img.naturalHeight !== 0) {
                            setBackgroundDark(fullUrl);
                            localStorage.setItem(`backgroundDark_${companyId || 'default'}`, fullUrl);
                        }
                    }, 0);
                }
            })
            .catch(() => {});
    }, []);

    const current =
        languageOptions.find((opt) => opt.value === i18n.language) ||
        languageOptions[0];

    const handleSelect = (opt) => {
        i18n.changeLanguage(opt.value);
        localStorage.setItem("language", opt.value);
        setOpen(false);
        window.location.reload();
    };

    // ✅ CORREÇÃO: Usar useMemo para recalcular o background quando as imagens forem carregadas
    const finalBackground = React.useMemo(() => {
        const hasValidLightBg = backgroundLight && backgroundLight.trim() !== '';
        const hasValidDarkBg = backgroundDark && backgroundDark.trim() !== '';
        
        if (currentMode === "light") {
            if (hasValidLightBg) {
                return `url(${backgroundLight})`;
            } else {
                return theme.palette.background ? theme.palette.background.default : "#f5f5f5";
            }
        } else {
            if (hasValidDarkBg) {
                return `url(${backgroundDark})`;
            } else {
                return theme.palette.background ? theme.palette.background.default : "#303030";
            }
        }
    }, [backgroundLight, backgroundDark, currentMode, theme.palette.background]);

    const getYoutubeVideoId = (url) => {
        if (!url || typeof url !== "string") return null;
        const trimmed = url.trim();
        const match = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
        return match ? match[1] : null;
    };
    const youtubeVideoId = getYoutubeVideoId(loginVideoYoutube);
    const videoPositionStyle = {
        "top-left": { top: 16, left: 16 },
        "top-right": { top: 16, right: 16 },
        "bottom-left": { bottom: 16, left: 16 },
        "bottom-right": { bottom: 16, right: 16 },
    }[loginVideoPosition] || { bottom: 16, right: 16 };

    return (
        <>
            <Helmet>
                <title>{appName || "Multi100"}</title>
                <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
            </Helmet>

            <div 
                className={classes.root}
                style={{
                    backgroundImage: typeof finalBackground === "string" && finalBackground.includes("url(") 
                        ? finalBackground 
                        : "none",
                    backgroundColor: typeof finalBackground === "string" && !finalBackground.includes("url(")
                        ? finalBackground
                        : "transparent"
                }}
            >
                {youtubeVideoId && (
                    <div
                        className={classes.loginVideoEmbed}
                        style={videoPositionStyle}
                    >
                        <iframe
                            title="Vídeo YouTube"
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=0`}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                )}
                {/* Seletor de idioma */}
                <div
                    ref={ref}
                    className={classes.languageSelector}
                >
                    <button
                        onClick={() => setOpen((o) => !o)}
                        className={classes.languageDropdown}
                    >
                        <img
                            src={current.icon}
                            alt={current.label}
                            className={classes.flagIcon}
                        />
                        {current.label}
                        <span>▾</span>
                    </button>

                    {open && (
                        <div className={classes.languageOptions}>
                            {languageOptions
                                .filter((opt) => enabledLanguages.includes(opt.value))
                                .map((opt) => (
                                    <button
                                        key={opt.value}
                                        onClick={() => handleSelect(opt)}
                                        className={classes.languageOption}
                                    >
                                        <img
                                            src={opt.icon}
                                            alt={opt.label}
                                            className={classes.flagIcon}
                                        />
                                        {opt.label}
                                    </button>
                                ))}
                        </div>
                    )}
                </div>

                <Container
                    component="main"
                    maxWidth={false}
                    className={classes.containerLogin}
                    style={{ 
                        zIndex: 10, 
                        position: "absolute", 
                        left: "50%", 
                        top: "50%", 
                        transform: "translate(-50%, -50%)",
                        boxSizing: "border-box",
                        margin: "0 auto"
                    }}
                >
                    <CssBaseline />
                    <div className={classes.paper}>
                        <IconButton
                            className={classes.iconButton}
                            onClick={colorMode?.toggleColorMode}
                        >
                            {currentMode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                        </IconButton>

                        <div>
                            <img className={classes.logoImg} alt="logo" />
                        </div>

                        <form className={classes.form} noValidate onSubmit={handlSubmit}>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                id="email"
                                label={i18n.t("login.form.email")}
                                name="email"
                                value={user.email}
                                onChange={handleChangeInput}
                                autoComplete="email"
                                autoFocus
                                className={classes.textField}
                            />
                            <TextField
                                variant="outlined"
                                margin="normal"
                                required
                                fullWidth
                                name="password"
                                label={i18n.t("login.form.password")}
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={user.password}
                                onChange={handleChangeInput}
                                autoComplete="current-password"
                                className={classes.textField}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={togglePasswordVisibility}
                                                edge="end"
                                                style={{ color: "#6b7280" }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                className={classes.submit}
                            >
                                {i18n.t("login.buttons.submit")}
                            </Button>
                            {allowSignup && (
                                <Grid container justifyContent="center">
                                    <Grid item>
                                        <Link
                                            href="#"
                                            variant="body2"
                                            component={RouterLink}
                                            to="/signup"
                                            className={classes.registerLink}
                                        >
                                            {i18n.t("login.buttons.register")}
                                        </Link>
                                    </Grid>
                                </Grid>
                            )}
                        </form>
                    </div>
                </Container>

                {/* Informação da versão */}
                <Typography className={classes.versionInfo}>
                    v{packageJson.version}
                </Typography>
            </div>
        </>
    );
};

export default Login;